"use client";

import Link from "next/link";
import { useState, KeyboardEvent, useEffect } from "react";
import { toast } from "react-toastify";
import { useSession } from "next-auth/react";
import { useQuery } from "@tanstack/react-query";
import { Message, Prisma } from "@prisma/client";
import { RiArrowRightCircleLine } from "react-icons/ri";
import { LuLoader2, LuTrash2 } from "react-icons/lu";

import { cn } from "~/utils/fontsConfig";
import { slugifyUser } from "~/utils/string";
import { GetEventMessagesOutput } from "~/types/ConversationTypes";
import { toastHandler } from "~/components/ToastHandler";
import { If } from "~/components/v3/Conditional";
import { Skeleton } from "~/components/v4/shadcn/Skeleton";
import { Button } from "~/components/v3/themed/Button";
import { TextArea } from "~/components/v3/themed/TextArea";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { LoginControlButton } from "~/components/v3/themed/LoginControlButton";

import {
    deleteMessage,
    sendMessage,
    canViewEventChat,
    getEventMessages,
    GetEventPermissionsCached,
} from "../actions";
import { CustomizableLayout } from "~/types/CustomizableLayout";

interface EventChatProps {
    event: Prisma.EventGetPayload<{
        select: {
            id: true;
            slug: true;
            eventJourneyStatus: true;
            groupChatUrl: true;
            attendees: {
                select: { status: true };
            };
            hosts: {
                select: {
                    user: { select: { id: true } };
                };
            };
            communityId: true;
            community: {
                select: {
                    id: true;
                    isHostAllowedToSMS: true;
                    isHostAllowedToEmail: true;
                };
            };
        };
    }>;
    permissions?: GetEventPermissionsCached;
    customization?: CustomizableLayout;
}

const canDeleteMessage = ({
    message,
    permissions,
    userId,
}: {
    message: Message;
    permissions: GetEventPermissionsCached;
    userId?: string;
}) => {
    if (permissions.isAdmin || permissions.isManager) return true;
    return message.userId === userId;
};

export function EventChat({
    event,
    permissions,
    customization,
}: EventChatProps) {
    if (!event || !permissions) return null;

    const { data: session } = useSession();
    const [message, setMessage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    const [chatMessages, setChatMessages] = useState<GetEventMessagesOutput>(
        [],
    );

    const { data: userCanChat } = useQuery({
        queryKey: ["canViewEventChat", event.id],
        queryFn: () => canViewEventChat(event.id),
    });

    const {
        data: conversationMessages,
        isLoading: isLoadingConversationMessages,
        refetch: refetchConversationMessages,
    } = useQuery({
        queryKey: ["conversationMessages", event.id],
        queryFn: () => getEventMessages(event.id),
        enabled: !!event.id && !!userCanChat?.showChat && !!session?.user.id,
        refetchInterval: 180000, // 3 minutes in miliseconds
    });

    const hostIds = event.hosts.map((h) => h.user.id);

    useEffect(() => {
        // replace the chat messages with new messages from conversation Messages
        // when conversation messages changes. Do this in place.
        // This is to prevent the chat from scrolling to the top every time a new message is sent.
        // This also deletes any messages that were deleted from the conversation.
        if (conversationMessages) {
            const newMessages = conversationMessages.filter(
                (newMsg) =>
                    !chatMessages.some(
                        (existingMsg) => existingMsg.id === newMsg.id,
                    ),
            );
            const deletedMessages = chatMessages.filter(
                (existingMsg) =>
                    !conversationMessages.some(
                        (newMsg) => newMsg.id === existingMsg.id,
                    ),
            );

            // remove deleted messages
            if (deletedMessages.length > 0) {
                setChatMessages(
                    chatMessages.filter(
                        (existingMsg) =>
                            !deletedMessages.some(
                                (deletedMsg) =>
                                    deletedMsg.id === existingMsg.id,
                            ),
                    ),
                );
            }

            if (newMessages.length > 0) {
                setChatMessages([...chatMessages, ...newMessages]);
            }

            // only show skeleton on first fetch
            if (isInitialLoad) {
                setIsInitialLoad(false);
            }
        }
    }, [conversationMessages]);

    const handleSendMessage = async () => {
        setLoading(true);
        toastHandler({
            loadingMessage: "Sending message...",
            successMessage: "Message sent!",
            errorMessage: "Error sending message!",
            operation: async () => {
                await sendMessage(message, event.id);
            },
            onSettled: () => {
                toast.dismiss("send-message");
                setMessage("");
                setLoading(false);
                refetchConversationMessages();
            },
        });
    };

    const handleDeleteMessage = async (messageId: string) => {
        toastHandler({
            loadingMessage: "Deleting message...",
            successMessage: "Message deleted!",
            errorMessage: "Error deleting message!",
            operation: async () => {
                await deleteMessage(messageId, event.slug);
            },
            onSettled: () => {
                toast.dismiss("delete-message");
                refetchConversationMessages();
            },
        });
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const isHost = (user?: { id: string } | null) => {
        if (!user) return false;
        return hostIds.includes(user.id);
    };

    if (!userCanChat?.showChat) {
        return (
            <div
                className={cn(
                    "flex flex-col gap-5 rounded-md bg-v3-N-600 p-6",
                    customization?.className,
                )}
            >
                <h2
                    className={cn(
                        "text-[20px] text-v3-N-300",
                        customization?.labelClassName,
                    )}
                >
                    Event Chat
                </h2>
                <div
                    className={cn(
                        "text-sm text-gray-500",
                        customization?.descriptionClassName,
                    )}
                >
                    {userCanChat?.message}
                </div>
                <If condition={!session?.user}>
                    <LoginControlButton
                        className="md:flex-row"
                        session={session}
                        customization={customization}
                    />
                </If>

                <div className="bg-opacity-60 p-0 blur-md backdrop-blur-md lg:p-4">
                    <div className="flex flex-col space-y-6">
                        {[1, 2].map((i) => (
                            <div
                                key={i}
                                className="flex w-full items-center justify-between gap-3"
                            >
                                <div className="flex w-full items-center gap-2">
                                    <Skeleton className="h-12 w-12 rounded-full" />
                                    <div className="flex w-full flex-col gap-2">
                                        <Skeleton className="h-3 w-24" />
                                        <div className="flex w-full items-center gap-2">
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-5 w-5" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                "flex flex-col gap-5 rounded-md bg-v3-N-600 p-6",
                customization?.className,
            )}
        >
            <div className="flex items-center justify-between">
                <h2
                    className={cn("text-[20px]", customization?.labelClassName)}
                >
                    Event Chat
                </h2>
                <Link
                    href={`/beta/events/${event.slug}/manage/messages`}
                    className="flex items-center justify-center p-[5px] text-v3-N300 hover:text-v3-N200"
                >
                    <RiArrowRightCircleLine className="h-5 w-5" />
                    <span className="sr-only">More</span>
                </Link>
            </div>

            <If condition={!!event.groupChatUrl}>
                <div className="flex flex-col gap-2">
                    <Link
                        href={event.groupChatUrl!}
                        target="_blank"
                        className="text-v3-primary underline underline-offset-4 hover:underline md:no-underline"
                    >
                        Contact us externally here
                    </Link>
                </div>
            </If>

            <div>
                {isLoadingConversationMessages && isInitialLoad ? (
                    <div className="flex flex-col space-y-6">
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="flex w-full items-center justify-between gap-3"
                            >
                                <div className="flex w-full items-center gap-2">
                                    <Skeleton className="h-12 w-12 rounded-full" />
                                    <div className="flex w-full flex-col gap-2">
                                        <Skeleton className="h-3 w-24" />
                                        <div className="flex w-full items-center gap-2">
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-5 w-5" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <div
                            className={cn(
                                "mb-4 flex text-sm",
                                customization?.descriptionClassName,
                            )}
                        >
                            Ask questions and talk with the hosts / other
                            attendees!
                        </div>
                        <div
                            className={cn(
                                "flex max-h-[300px] flex-col gap-5 overflow-y-scroll",
                                chatMessages?.length ? "pb-10" : "",
                            )}
                        >
                            {chatMessages?.map((message) => (
                                <div
                                    key={message.id}
                                    className="flex w-full items-center justify-between gap-3"
                                >
                                    <div className="flex items-center gap-2">
                                        <UserAvatar
                                            user={{
                                                name: message?.user?.name,
                                                image: message?.user?.image,
                                            }}
                                            className="h-10 w-10 rounded-full"
                                        />

                                        <div className="flex flex-col">
                                            <Link
                                                href={
                                                    message?.user
                                                        ? `/profile/${slugifyUser(
                                                              {
                                                                  name: message
                                                                      .user
                                                                      .name,
                                                                  id: message
                                                                      .user.id,
                                                                  slug: message
                                                                      .user
                                                                      .slug,
                                                              },
                                                          )}`
                                                        : "#"
                                                }
                                                target="_blank"
                                                className={cn(
                                                    "cursor-pointer text-xs hover:underline",
                                                    isHost(message?.user) &&
                                                        "text-secondary",
                                                )}
                                            >
                                                {isHost(message?.user) &&
                                                    "Host - "}
                                                {message?.user?.name}
                                            </Link>
                                            <p
                                                className={cn(
                                                    "text-sm",
                                                    message.text?.length > 30
                                                        ? "max-w-[500px] break-words break-all"
                                                        : "",
                                                )}
                                            >
                                                {message.text}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() =>
                                            handleDeleteMessage(message.id)
                                        }
                                        className="cursor-pointer text-v3-N-300 hover:text-white"
                                        disabled={
                                            !canDeleteMessage({
                                                message,
                                                permissions,
                                                userId: session?.user?.id,
                                            })
                                        }
                                    >
                                        <LuTrash2 className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {chatMessages.length < 200 && (
                            <div className="mt-4 flex w-full flex-col space-y-4">
                                <TextArea
                                    placeholder="Type a message..."
                                    className={cn(
                                        "h-[60px] w-full rounded-[10px] placeholder:not-italic",
                                        customization?.inputClassName,
                                    )}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                />

                                <div className="flex justify-end">
                                    <Button
                                        variant="secondary"
                                        className=""
                                        onClick={handleSendMessage}
                                        disabled={message.length === 0}
                                    >
                                        {loading ? (
                                            <div className="flex items-center gap-2">
                                                <LuLoader2 className="h-4 w-4 animate-spin" />
                                                <span className="sr-only">
                                                    Sending...
                                                </span>
                                            </div>
                                        ) : (
                                            "Send message"
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
